<template>
  <div class="super-container">
    <MainMenu class="main-menu" />
    <Header class="header" />
    <SubHeader class="sub-header" />
    <MainArea
      :animation-name="animationName"
      class="main-area"
    />
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import { subModules } from '@/modules/subModules';
import MutationTypes from '@/store/constants/mutations';
import defaultAvatar from '@/assets/images/default/avatar.png';
import MainArea from '@/components/layout/main/MainArea.vue';
import GetterTypes from '@/store/constants/getters';
import SubHeader from '@/components/layout/header/SubHeader.vue';
import ActionTypes from '@/store/constants/actions';
import moduleIcons from '@/data/moduleIcons/moduleIcons';
import MainMenu from './layout/navigation/menu/MainMenu.vue';
import Header from './layout/header/Header.vue';

export default {
  name: 'LoggedIn',
  components: {
    SubHeader,
    MainArea,
    MainMenu,
    Header,
  },
  beforeRouteUpdate(to, from, next) {
    if (from.meta.noTransition && to.meta.noTransition) {
      this.animationName = '';
    } else {
      this.animationName = 'main-view';
    }
    next();
  },
  data() {
    return {
      animationName: 'main-view',
    };
  },
  computed: {
    ...mapGetters([
      GetterTypes.ARE_MODULES_ALREADY_LOADED,
      GetterTypes.IS_BASIC_INFO_ALREADY_LOADED,
      GetterTypes.MENU_EVENT_BUS,
    ]),
  },
  mounted() {
    this.configureAxios();
    this.loadSyncData();
    this.loadAsyncData();
  },
  methods: {
    configureAxios() {
      this.$axios.defaults.headers.common.Authorization = `Bearer ${this.$store.getters.jwt}`;
      this.$axios.interceptors.response.use(null, (error) => {
        if (error.response.status === 401) {
          this.$store.commit(MutationTypes.SIGN_OUT);
          this.$router.push('/login');
          delete this.$axios.defaults.headers.common.Authorization;
        }
        return Promise.reject(error);
      });
    },
    loadSyncData() {
      const promises = [
        this.loadInitialInfo(),
        this.loadSearchableInfo(),
        this.loadAuthority(),
      ];

      Promise.all(promises)
        .then(() => {
          this.$store.commit(MutationTypes.MARK_IS_ALL_LOADED);
        })
        .then(this.expandMenuTreeByBreadcrumbs);
    },
    loadInitialInfo() {
      if (!this.isBasicInfoAlreadyLoaded) {
        return this.$axios.get('get_initial_info')
          .then((resp) => {
            this.$store.commit(MutationTypes.SET_BASIC_INFO, resp.data);
            this.$store.commit(MutationTypes.SET_USERNAME, resp.data.userName);
          })
          .catch((e) => console.error(e));
      }
      return Promise.resolve();
    },
    loadAvatar() {
      return this.$axios.get('get_user_image')
        .then(({ data }) => {
          this.$store.commit(MutationTypes.SET_BASE64_AVATAR, data || defaultAvatar);
        }).catch(() => {
          this.$toasted.error('Kunde inte hämta avatar!', {
            duration: 3000,
          });
        });
    },
    loadSearchableInfo() {
      return this.$axios.get('get_searchable_info')
        .then(({ data }) => {
          this.$store.commit(MutationTypes.SET_SEARCHABLE_INFO, data);
        });
    },
    loadAuthority() {
      if (!this.areModulesAlreadyLoaded) {
        return this.$axios.get('auth/authority').then((resp) => {
          const authority = resp.data
            .map((auth) => ({
              ...auth,
              icon: moduleIcons[auth.icon] || moduleIcons.fallback,
            }));

          authority
            .map((auth) => auth.modules)
            .forEach(this.enrichAuthModules);

          this.$store.commit(MutationTypes.SET_MODULE_CATEGORIES, authority);
        }).catch();
      }
      return Promise.resolve();
    },
    enrichAuthModules(authModules) {
      authModules.forEach((authModule) => {
        if (authModule.subAuthorityModules) {
          authModule.subAuthorityModules.forEach((subModule) => {
            const { key } = subModule;
            const routerComponent = subModules[key];

            if (routerComponent) {
              subModule.tags = routerComponent.tags;
            } else {
              console.warn('Exists in backend request, but not in frontend registry: %o',
                `[${subModule.key}] ${authModule.moduleName}/${subModule.moduleName}`);
            }
          });
        }
      });
    },
    loadEventCategories() {
      return this.$axios.get('get_eventgenres')
        .then(({ data }) => {
          const eventCategoriesWithMappedNiches = data.eventCategories.map((eventCategory) => {
            const remappedEventCategory = eventCategory;
            remappedEventCategory.niches = data.eventNiches.filter((niche) => niche.eventCategoryId === eventCategory.id);
            return remappedEventCategory;
          });
          this.$store.commit(MutationTypes.SET_EVENT_CATEGORIES, eventCategoriesWithMappedNiches);
        });
    },
    expandMenuTreeByBreadcrumbs() {
      const { breadcrumbs } = this.$store.getters;

      const categoryBreadcrumb = breadcrumbs[0];
      this.menuEventBus.emit('category-clicked',
        {
          except: categoryBreadcrumb.name,
          modules: categoryBreadcrumb.numberOfChildren,
        });

      const moduleBreadcrumb = breadcrumbs[1];
      this.menuEventBus.emit('module-clicked', {
        except: moduleBreadcrumb.key,
        subModules: moduleBreadcrumb.numberOfChildren,
      });
    },
    loadAsyncData() {
      this.$store.dispatch(ActionTypes.LOAD_MASTER_ARENAS);
      this.loadAvatar();
      this.loadEventCategories();
    },
  },
};
</script>

<style scoped lang="scss">
  .super-container {
    position: relative;
    display: grid;
    height: 100vh;
    grid-template-areas: "header header" "sidebar sub-header" "sidebar main";
    grid-template-columns: 280px 1fr;
    grid-template-rows: 60px min-content 1fr;

    @media (max-width: $mediumLargeBreakpoint) {
      grid-template-areas: "header" "sub-header" "main";
      grid-template-columns: 1fr;
      grid-template-rows: 60px min-content 1fr;
    }

    .main-menu {
      grid-area: sidebar;
    }

    .header {
      grid-area: header;
    }

    .sub-header {
      grid-area: sub-header;
    }

    .main-area {
      grid-area: main;
    }
  }
</style>
